import { fetchPhysicians, formatPhysicianOptionLabel } from '@/entities/physician';
import {
  CARDIO_ONE_DEVICE_MODEL_LIST,
  DEVICE_MODEL_LIST,
  ORGANIZATION_SLUG,
  VIRTU_OX_DEVICE_MODEL_LIST,
} from '@/shared/config';
import { STUDY_TYPES } from '@/entities/study';
import { auth } from '@/shared/lib';
import { t } from 'ttag';
import type {
  Physician,
  PhysicianFetchFilter,
  PhysicianOption,
  RenderNotFoundContentProps,
} from '../ui/newStudyStep3/newStudyStep3.types';

/**
 * Renders the empty-state text of the new-study device-id search dropdown. Shows a loading label while a search is in flight, a no-device message once a searched id yields nothing, and a prompt to enter a device id before any search has run.
 *
 * @param isSearching - Whether a device search is currently in flight.
 * @param searchDeviceId - Device id text that was searched for.
 * @returns The empty-state span for the device select.
 */
const renderNotFoundContent = ({ isSearching, searchDeviceId }: RenderNotFoundContentProps) =>
  isSearching ? (
    <span className="not-found-content">{`${t`Loading`}...`}</span>
  ) : searchDeviceId ? (
    <span className="not-found-content">{t`There is no device`}</span>
  ) : (
    <span className="not-found-content">{t`Enter device ID to search`}</span>
  );

/**
 * Maps a study type to its sample report PDF on the public Bioflux CDN for the new-study wizard. Returns dedicated entries for MCT, cardiac event, Holter, extended Holter (which reuses the Holter file name) and MCT Peek, and null for any other study type.
 *
 * @param studyType - Study type whose sample report is requested.
 * @returns The sample report url and file name, or null when the study type has none.
 */
const generateSampleReportPdfData = (studyType: any) => {
  switch (studyType) {
    case STUDY_TYPES.MCT: {
      return {
        url: 'https://public.cdn.biotricity.com/sample-reports/MCT.pdf',
        fileName: 'MCT.pdf',
      };
    }
    case STUDY_TYPES.CARDIAC_EVENT: {
      return {
        url: 'https://public.cdn.biotricity.com/sample-reports/Cardiac+Event.pdf',
        fileName: 'Cardiac Event.pdf',
      };
    }
    case STUDY_TYPES.HOLTER: {
      return {
        url: 'https://public.cdn.biotricity.com/sample-reports/Holter.pdf',
        fileName: 'Holter.pdf',
      };
    }
    case STUDY_TYPES.EXTENDED_HOLTER: {
      return {
        url: 'https://public.cdn.biotricity.com/sample-reports/Extended+Holter.pdf',
        fileName: 'Holter.pdf',
      };
    }
    case STUDY_TYPES.MCT_PEEK: {
      return {
        url: 'https://public.cdn.biotricity.com/sample-reports/MCT+Peek.pdf',
        fileName: 'MCT Peek.pdf',
      };
    }
    default: {
      return null;
    }
  }
};

/**
 * Decides whether the study-type warning text of new-study step 3 applies to the chosen type. Suppresses it for extended Holter and shows it only for MCT and MCT Peek.
 *
 * @param studyType - Study type selected in the wizard.
 * @returns True when the study type warning should be displayed.
 */
const shouldShowStudyTypeWarningText = (studyType: string) =>
  studyType === 'EXTENDED HOLTER' ? false : !!(studyType === 'MCT' || studyType === 'MCT PEEK');

/**
 * Converts fetched physicians into select options for the new-study physician pickers. Labels each option through formatPhysicianOptionLabel, uses the physician id as the value and carries the first and last name along on the option.
 *
 * @param physicians - Physicians returned from the API.
 * @returns Physician select options, or undefined when no physicians were given.
 */
const physiciansFormatter = (physicians?: Physician[]) =>
  physicians?.map((physician) => ({
    label: formatPhysicianOptionLabel(physician),
    value: physician.id,
    firstName: physician.firstName,
    lastName: physician.lastName,
  }));

/**
 * Fetches a page of ten referring or interpreting physicians for a facility, filtered by name or NPI, and returns the state slice for that picker. Interpreting requests also require a username, load-more requests continue from the last option's id as cursor and append to the existing options, the has-more flag is set when a full page came back, and a failed fetch is logged while keeping the existing options with has-more false.
 *
 * @param type - Which physician picker is being filled.
 * @param facilityId - Facility the physicians must belong to.
 * @param searchText - Name or NPI fragment to filter by.
 * @param isLoadMore - Whether the page is appended to the existing options.
 * @param existingOptions - Options already loaded, used for the cursor and the append.
 * @returns The options list and has-more flag keyed for the requested physician type.
 */
const handleFetchPhysiciansWithSearch = async ({
  type,
  facilityId,
  searchText = '',
  isLoadMore = false,
  existingOptions = [],
}: {
  type: 'referring' | 'interpreting';
  facilityId: string;
  searchText?: string;
  isLoadMore?: boolean;
  existingOptions?: PhysicianOption[];
}) => {
  const optionsKey =
    type === 'referring' ? 'referringPhysiciansOptions' : 'interprettingPhysiciansOptions';
  const hasMoreKey =
    type === 'referring' ? 'hasMoreReferringPhysicians' : 'hasMoreInterprettingPhysicians';

  try {
    const filter: PhysicianFetchFilter = { facilityId };

    // search by name or NPI
    if (searchText) filter.nameOrNpi = searchText;
    if (type === 'interpreting') filter.hasUsername = true;
    if (isLoadMore && existingOptions.length > 0) {
      filter.cursor = existingOptions[existingOptions.length - 1].value;
    }

    const physicians = await fetchPhysicians(filter, 10);
    const convertedPhysicians =
      physiciansFormatter(
        physicians
          ?.filter((physician): physician is NonNullable<typeof physician> => physician !== null)
          .map((physician) => ({
            ...physician,
            firstName: physician.firstName ?? '',
            lastName: physician.lastName ?? '',
            npi: physician.npi ?? '',
            contact: physician.contact ?? { fax: null, nightPhone: null, phone1: null },
          })),
      ) ?? [];

    const options = isLoadMore ? [...existingOptions, ...convertedPhysicians] : convertedPhysicians;

    // If fetched < 10 items, no more data to load
    const hasMore = convertedPhysicians.length >= 10;

    return {
      [optionsKey]: options,
      [hasMoreKey]: hasMore,
    };
  } catch (error) {
    console.error(`Failed to fetch ${type} physicians:`, error);
    return {
      [optionsKey]: isLoadMore ? existingOptions : [],
      [hasMoreKey]: false,
    };
  }
};

/**
 * Picks the device model list offered on step 3 of a new study for the current organization and
 * facility. Returns the CardioOne list for a CardioOne organization, the VirtuOx list for a
 * non-test, non-Bioflux Direct facility that can use ICOR, and the default list otherwise.
 *
 * @param info - Facility flags carrying isTest, isBiofluxDirect and canUseIcor.
 * @returns The device model options to show.
 */
const getDeviceModelList = (info?: {
  isTest: boolean;
  isBiofluxDirect: boolean;
  canUseIcor: boolean;
}) => {
  const { isTest, isBiofluxDirect, canUseIcor } = info || {};
  if (auth.userOrganization()?.slug === ORGANIZATION_SLUG.CARDIO_ONE) {
    return CARDIO_ONE_DEVICE_MODEL_LIST;
  }
  if (!isTest && !isBiofluxDirect && canUseIcor) {
    return VIRTU_OX_DEVICE_MODEL_LIST;
  }
  return DEVICE_MODEL_LIST;
};

export {
  generateSampleReportPdfData,
  getDeviceModelList,
  handleFetchPhysiciansWithSearch,
  physiciansFormatter,
  renderNotFoundContent,
  shouldShowStudyTypeWarningText,
};
